/**
 * PublicInquiryPage — E-102 P2-4 public contact form
 *
 * Posts to POST /api/v1/public/inquiry (P1-6 backend).
 * hCaptcha verified, honeypot + time-heuristic anti-spam.
 * No auth required — public route.
 */

import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
  captchaToken: string;
  websiteUrl: string;
}

type PageState = 'form' | 'submitting' | 'success' | 'error';

const HCAPTCHA_SITE_KEY =
  import.meta.env.VITE_HCAPTCHA_SITE_KEY ?? '10000000-ffff-ffff-ffff-000000000001';

export default function PublicInquiryPage() {
  const { t } = useTranslation();
  const formStartTs = useRef(Date.now());
  const captchaRef = useRef<HTMLDivElement>(null);
  const [pageState, setPageState] = useState<PageState>('form');
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    message: '',
    captchaToken: '',
    websiteUrl: '',
  });

  useEffect(() => {
    if (document.querySelector('script[src*="hcaptcha"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const tryRender = () => {
      const w = window as unknown as Record<string, unknown>;
      if (w['hcaptcha'] && captchaRef.current && !captchaRef.current.dataset['rendered']) {
        captchaRef.current.dataset['rendered'] = '1';
        (
          w['hcaptcha'] as { render: (el: HTMLElement, cfg: Record<string, unknown>) => void }
        ).render(captchaRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          callback: (token: string) => setForm(prev => ({ ...prev, captchaToken: token })),
        });
      } else {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();
  }, []);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (form.name.trim().length < 2) errs['name'] = t('inquiry.validation.nameMin');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs['email'] = t('inquiry.validation.email');
    if (form.message.trim().length < 10) errs['message'] = t('inquiry.validation.messageMin');
    if (form.message.length > 5000) errs['message'] = t('inquiry.validation.messageMax');
    if (!form.captchaToken) errs['captcha'] = t('inquiry.validation.captcha');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      setPageState('submitting');
      try {
        const res = await fetch('/api/v1/public/inquiry', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || undefined,
            message: form.message.trim(),
            captchaToken: form.captchaToken,
            websiteUrl: form.websiteUrl,
            formStartTs: formStartTs.current,
          }),
        });
        if (!res.ok && res.status !== 202) {
          throw new Error(`${res.status}`);
        }
        setPageState('success');
      } catch {
        setErrorMsg(t('inquiry.error.submit'));
        setPageState('error');
      }
    },
    [form, validate, t]
  );

  const set =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  if (pageState === 'success') {
    return (
      <div className="mx-auto my-16 max-w-lg rounded-lg border bg-card p-8 text-center shadow-sm">
        <h1 className="text-2xl font-semibold">{t('inquiry.success.title')}</h1>
        <p className="mt-3 text-muted-foreground">{t('inquiry.success.body')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto my-12 max-w-lg rounded-lg border bg-card p-8 shadow-sm">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{t('inquiry.title')}</h1>
        <p className="mt-1 text-muted-foreground">{t('inquiry.subtitle')}</p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="inq-name" className="mb-1 block text-sm font-medium">
            {t('inquiry.field.name')} *
          </label>
          <input
            id="inq-name"
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={form.name}
            onChange={set('name')}
          />
          {fieldErrors['name'] && (
            <p className="mt-1 text-xs text-destructive">{fieldErrors['name']}</p>
          )}
        </div>

        <div>
          <label htmlFor="inq-email" className="mb-1 block text-sm font-medium">
            {t('inquiry.field.email')} *
          </label>
          <input
            id="inq-email"
            type="email"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={form.email}
            onChange={set('email')}
          />
          {fieldErrors['email'] && (
            <p className="mt-1 text-xs text-destructive">{fieldErrors['email']}</p>
          )}
        </div>

        <div>
          <label htmlFor="inq-phone" className="mb-1 block text-sm font-medium">
            {t('inquiry.field.phone')}
          </label>
          <input
            id="inq-phone"
            type="tel"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={form.phone}
            onChange={set('phone')}
          />
        </div>

        <div>
          <label htmlFor="inq-message" className="mb-1 block text-sm font-medium">
            {t('inquiry.field.message')} *
          </label>
          <textarea
            id="inq-message"
            rows={6}
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={form.message}
            onChange={set('message')}
          />
          {fieldErrors['message'] && (
            <p className="mt-1 text-xs text-destructive">{fieldErrors['message']}</p>
          )}
        </div>

        {/* Honeypot — bots fill this, humans never see it */}
        <div style={{ display: 'none' }} aria-hidden="true">
          <label>Website</label>
          <input type="text" tabIndex={-1} value={form.websiteUrl} onChange={set('websiteUrl')} />
        </div>

        <div ref={captchaRef} className="my-4" />
        {fieldErrors['captcha'] && (
          <p className="text-xs text-destructive">{fieldErrors['captcha']}</p>
        )}

        <Button type="submit" disabled={pageState === 'submitting'} className="w-full">
          {pageState === 'submitting' ? t('inquiry.submitting') : t('inquiry.submit')}
        </Button>

        {pageState === 'error' && <p className="text-sm text-destructive">{errorMsg}</p>}
      </form>
    </div>
  );
}
